import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "./config";
import "./index.css";

/* ============================================================================
 * I-PQSign — Premium Enterprise Single-Screen Experience
 * ========================================================================== */

const LANGS = {
  en: {
    label: "English",
    flag: "EN",
    dict: {
      tagline: "Digital Trust Platform",
      heroA: "Secure Every Signature.",
      heroB: "Trust Every",
      heroC: " Transaction.",
      heroDesc: "I-PQSign combines post-quantum cryptography, digital signatures, and blockchain anchoring to future-proof your critical documents and workflows.",
      cap1Title: "Post-Quantum Security",
      cap1Desc: "PQC algorithms to protect against future quantum threats.",
      cap2Title: "Blockchain Anchored",
      cap2Desc: "Immutable proof of existence on trusted blockchains.",
      cap3Title: "Digital Signatures",
      cap3Desc: "Legally valid, tamper-proof e-signatures for all documents.",
      cap4Title: "Enterprise Grade",
      cap4Desc: "Built for compliance, scalability and mission-critical workflows.",
      welcome: "Welcome Back",
      signinSub: "Sign in to continue to I-PQSign",
      emailLabel: "Email / Username",
      emailPh: "Enter your email or username",
      passLabel: "Password",
      passPh: "Enter your password",
      remember: "Remember Me",
      forgot: "Forgot Password?",
      signin: "Sign In",
      continueWith: "or continue with",
      msSSO: "Microsoft SSO",
      gSSO: "Google SSO",
      samlSSO: "SAML / SSO Login",
      enterprise: "Enterprise Login",
      newTo: "New to I-PQSign?",
      contactAdmin: "Request Access",
      needHelp: "Need Help?",
      copyright: "© 2026 Intrust Innovation Labs. All rights reserved.",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      support: "Support",
      badge1Title: "Enterprise Grade",
      badge1Sub: "Security",
      badge2Title: "ISO 27001",
      badge2Sub: "Certified",
      badge3Title: "Made in India",
      badge3Sub: "Trusted Globally"
    }
  },
  de: {
    label: "Deutsch",
    flag: "DE",
    dict: {
      tagline: "Quanten-Risiko-Intelligenz-Plattform",
      heroA: "Heute sicher.",
      heroB: "Quantenbereit",
      heroC: " für morgen.",
      heroDesc: "End-to-End-Sichtbarkeit Ihrer kryptografischen Assets, Risiken und Bereitschaft für die Post-Quanten-Welt.",
      cap1Title: "Entdecken. Bewerten. Schützen.",
      cap1Desc: "Verborgene Risiken in Anwendungen, Netzwerken, Cloud, OT und Drittanbieter-Ökosystemen aufdecken.",
      cap2Title: "Quanten-Risikoanalyse",
      cap2Desc: "KI-Einblicke zur Priorisierung von Risiken und Stärkung Ihrer Quantenresilienz.",
      cap3Title: "Compliance & Governance",
      cap3Desc: "Konform mit NIST PQC, ISO 27001, DPDP, CERT-In und globalen Standards.",
      cap4Title: "Zukunftssicheres Unternehmen",
      cap4Desc: "Reduzieren Sie HNDL-Risiken und schützen Sie langfristig sensible Daten.",
      welcome: "Willkommen zurück",
      signinSub: "Melden Sie sich bei I-PQSign an",
      emailLabel: "E-Mail / Benutzername",
      emailPh: "E-Mail oder Benutzername eingeben",
      passLabel: "Passwort",
      passPh: "Passwort eingeben",
      remember: "Angemeldet bleiben",
      forgot: "Passwort vergessen?",
      signin: "Anmelden",
      continueWith: "oder weiter mit",
      msSSO: "Microsoft SSO",
      gSSO: "Google SSO",
      samlSSO: "SAML / SSO Login",
      enterprise: "Enterprise-Login",
      newTo: "Neu bei I-PQSign?",
      contactAdmin: "Zugang anfordern",
      needHelp: "Hilfe?",
      copyright: "© 2026 Intrust Innovation Labs. Alle Rechte vorbehalten.",
      privacy: "Datenschutz",
      terms: "Nutzungsbedingungen",
      support: "Support",
      badge1Title: "Enterprise-Grade",
      badge1Sub: "Sicherheit",
      badge2Title: "ISO 27001",
      badge2Sub: "Zertifiziert",
      badge3Title: "Made in India",
      badge3Sub: "Weltweit vertraut"
    }
  },
  fr: {
    label: "Français",
    flag: "FR",
    dict: {
      tagline: "Plateforme d'intelligence des risques quantiques",
      heroA: "Sécurisé aujourd'hui.",
      heroB: "Prêt pour le quantique",
      heroC: " demain.",
      heroDesc: "Visibilité de bout en bout sur vos actifs cryptographiques, risques et préparation au post-quantique.",
      cap1Title: "Découvrir. Évaluer. Protéger.",
      cap1Desc: "Découvrez les risques cachés dans les applications, réseaux, cloud, OT et écosystèmes tiers.",
      cap2Title: "Analyse des risques quantiques",
      cap2Desc: "Insights IA pour prioriser les risques et renforcer la résilience quantique.",
      cap3Title: "Conformité & Gouvernance",
      cap3Desc: "Alignement avec NIST PQC, ISO 27001, DPDP, CERT-In et standards mondiaux.",
      cap4Title: "Pérennisez votre entreprise",
      cap4Desc: "Atténuez les risques HNDL et protégez les données sensibles sur le long terme.",
      welcome: "Bon retour",
      signinSub: "Connectez-vous à I-PQSign",
      emailLabel: "Email / Nom d'utilisateur",
      emailPh: "Entrez votre email ou nom d'utilisateur",
      passLabel: "Mot de passe",
      passPh: "Entrez votre mot de passe",
      remember: "Se souvenir de moi",
      forgot: "Mot de passe oublié ?",
      signin: "Se connecter",
      continueWith: "ou continuer avec",
      msSSO: "Microsoft SSO",
      gSSO: "Google SSO",
      samlSSO: "Connexion SAML / SSO",
      enterprise: "Connexion entreprise",
      newTo: "Nouveau sur I-PQSign ?",
      contactAdmin: "Demander un accès",
      needHelp: "Aide ?",
      copyright: "© 2026 Intrust Innovation Labs. Tous droits réservés.",
      privacy: "Confidentialité",
      terms: "Conditions d'utilisation",
      support: "Support",
      badge1Title: "Qualité entreprise",
      badge1Sub: "Sécurité",
      badge2Title: "ISO 27001",
      badge2Sub: "Certifié",
      badge3Title: "Made in India",
      badge3Sub: "Reconnu mondialement"
    }
  },
  es: {
    label: "Español",
    flag: "ES",
    dict: {
      tagline: "Plataforma de Inteligencia de Riesgo Cuántico",
      heroA: "Seguro hoy.",
      heroB: "Listo para lo cuántico",
      heroC: " mañana.",
      heroDesc: "Visibilidad integral de tus activos criptográficos, riesgos y preparación para la era post-cuántica.",
      cap1Title: "Descubrir. Evaluar. Proteger.",
      cap1Desc: "Descubre riesgos ocultos en aplicaciones, redes, nube, OT y ecosistemas de terceros.",
      cap2Title: "Análisis de Riesgo Cuántico",
      cap2Desc: "Insights con IA para priorizar riesgos y fortalecer la resiliencia cuántica.",
      cap3Title: "Cumplimiento y Gobernanza",
      cap3Desc: "Alineado con NIST PQC, ISO 27001, DPDP, CERT-In y estándares globales.",
      cap4Title: "Prepara tu empresa para el futuro",
      cap4Desc: "Mitiga los riesgos HNDL y protege datos sensibles a largo plazo.",
      welcome: "Bienvenido de nuevo",
      signinSub: "Inicia sesión en I-PQSign",
      emailLabel: "Email / Usuario",
      emailPh: "Ingresa tu email o usuario",
      passLabel: "Contraseña",
      passPh: "Ingresa tu contraseña",
      remember: "Recordarme",
      forgot: "¿Olvidaste tu contraseña?",
      signin: "Iniciar sesión",
      continueWith: "o continúa con",
      msSSO: "Microsoft SSO",
      gSSO: "Google SSO",
      samlSSO: "Inicio SAML / SSO",
      enterprise: "Login empresarial",
      newTo: "¿Nuevo en I-PQSign?",
      contactAdmin: "Solicitar acceso",
      needHelp: "¿Ayuda?",
      copyright: "© 2026 Intrust Innovation Labs. Todos los derechos reservados.",
      privacy: "Privacidad",
      terms: "Términos de uso",
      support: "Soporte",
      badge1Title: "Nivel empresarial",
      badge1Sub: "Seguridad",
      badge2Title: "ISO 27001",
      badge2Sub: "Certificado",
      badge3Title: "Hecho en India",
      badge3Sub: "Confianza global"
    }
  }
};

const FONT_STACK = 'Inter, Geist, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

const colors = {
  bg: "#070815",
  bgTop: "#0b0c22",
  panel: "rgba(17, 19, 45, 0.65)",
  panelBorder: "rgba(139, 92, 246, 0.22)",
  text: "#e9eaff",
  textMuted: "#9aa0c7",
  accent: "#8b5cf6",
  accent2: "#6366f1",
  accentSoft: "rgba(139, 92, 246, 0.15)",
  inputBg: "rgba(10, 12, 32, 0.75)",
  inputBorder: "rgba(139, 92, 246, 0.28)",
  inputBorderFocus: "rgba(167, 139, 250, 0.9)",
};

const Icon = ({ name, size = 18, color = "currentColor", strokeWidth = 1.8 }) => {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "shield": return <svg {...common}><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" /></svg>;
    case "shield-pulse": return <svg {...common}><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" /><path d="M8 12l2 2 2-4 2 4 2-2" /></svg>;
    case "atom": return <svg {...common}><circle cx="12" cy="12" r="1.5" /><ellipse cx="12" cy="12" rx="10" ry="4" /><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(60 12 12)" /><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(120 12 12)" /></svg>;
    case "compliance": return <svg {...common}><path d="M12 2l9 4v6c0 5-3.5 9-9 10-5.5-1-9-5-9-10V6l9-4z" /><path d="M9 12l2 2 4-4" /></svg>;
    case "lock": return <svg {...common}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>;
    case "mail": return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>;
    case "eye": return <svg {...common}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>;
    case "eye-off": return <svg {...common}><path d="M3 3l18 18" /><path d="M10.6 6.1A10.9 10.9 0 0112 6c6.5 0 10 6 10 6a17 17 0 01-3.2 3.9M6.1 6.1C3.4 8 2 12 2 12s3.5 6 10 6a10.9 10.9 0 004.4-.9" /><path d="M9.9 9.9a3 3 0 104.2 4.2" /></svg>;
    case "globe": return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" /></svg>;
    case "help": return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 4" /><circle cx="12" cy="17" r="0.6" fill={color} /></svg>;
    case "arrow": return <svg {...common}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    case "chevron": return <svg {...common}><path d="M6 9l6 6 6-6" /></svg>;
    case "check": return <svg {...common}><path d="M5 12l4 4L19 6" /></svg>;
    case "x": return <svg {...common}><path d="M6 6l12 12M18 6L6 18" /></svg>;
    case "iso": return <svg {...common}><rect x="4" y="6" width="16" height="12" rx="2" /><path d="M9 10v4M12 10v4M15 10v4" /></svg>;
    case "spark": return <svg {...common}><path d="M12 2v6M12 16v6M2 12h6M16 12h6M5 5l4 4M15 15l4 4M19 5l-4 4M9 15l-5 4" /></svg>;
    default: return null;
  }
};

const BrandMark = ({ size = 38 }) => (
  <motion.svg width={size} height={size} viewBox="0 0 64 64" initial={{ rotate: -6, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} transition={{ duration: 0.6, ease: "easeOut" }} whileHover={{ scale: 1.08, rotate: 4 }} style={{ filter: "drop-shadow(0 6px 18px rgba(139,92,246,0.5))" }}>
    <defs>
      <linearGradient id="bm" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#a78bfa" /><stop offset="100%" stopColor="#6366f1" /></linearGradient>
    </defs>
    <path d="M32 4l24 10v14c0 14-10 24-24 32C18 52 8 42 8 28V14L32 4z" fill="url(#bm)" opacity="0.95" />
    <text x="32" y="40" textAnchor="middle" fontSize="22" fontWeight="800" fill="white" fontFamily={FONT_STACK}>Q</text>
  </motion.svg>
);

const QuantumLock = () => {
  const particles = useMemo(() => Array.from({ length: 24 }, (_, i) => ({ id: i, angle: (i / 24) * Math.PI * 2, radius: 80 + Math.random() * 80, size: 1 + Math.random() * 2, delay: Math.random() * 4, dur: 4 + Math.random() * 5 })), []);
  const floaters = useMemo(() => Array.from({ length: 16 }, (_, i) => ({ id: i, x: Math.random() * 100, y: Math.random() * 100, s: 1 + Math.random() * 2, d: 4 + Math.random() * 6, del: Math.random() * 5 })), []);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", maxHeight: 360, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(139,92,246,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.07) 1px, transparent 1px)", backgroundSize: "36px 36px", maskImage: "radial-gradient(circle at center, rgba(0,0,0,0.9) 25%, rgba(0,0,0,0) 75%)", WebkitMaskImage: "radial-gradient(circle at center, rgba(0,0,0,0.9) 25%, rgba(0,0,0,0) 75%)" }} />
      {floaters.map((f) => <motion.div key={f.id} style={{ position: "absolute", left: `${f.x}%`, top: `${f.y}%`, width: f.s, height: f.s, borderRadius: "50%", background: "rgba(167,139,250,0.7)", boxShadow: "0 0 8px rgba(167,139,250,0.7)" }} animate={{ y: [-10, 10, -10], opacity: [0.2, 1, 0.2] }} transition={{ duration: f.d, delay: f.del, repeat: Infinity, ease: "easeInOut" }} />)}
      <motion.div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", border: "1px solid rgba(139,92,246,0.22)", bottom: 30 }} animate={{ rotate: 360 }} transition={{ duration: 26, repeat: Infinity, ease: "linear" }} />
      <motion.div style={{ position: "absolute", width: 220, height: 220, borderRadius: "50%", border: "1px dashed rgba(167,139,250,0.35)", bottom: 70 }} animate={{ rotate: -360 }} transition={{ duration: 18, repeat: Infinity, ease: "linear" }} />
      <motion.div style={{ position: "absolute", width: 150, height: 150, borderRadius: "50%", border: "1px solid rgba(99,102,241,0.45)", bottom: 105, boxShadow: "0 0 40px rgba(99,102,241,0.3) inset" }} animate={{ scale: [1, 1.05, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
      <svg width="400" height="400" viewBox="-200 -200 400 400" style={{ position: "absolute", inset: 0, margin: "auto" }}>
        {particles.map((p) => <motion.circle key={p.id} cx={Math.cos(p.angle) * p.radius} cy={Math.sin(p.angle) * p.radius} r={p.size} fill="#a78bfa" animate={{ opacity: [0, 1, 0], scale: [0.6, 1.4, 0.6] }} transition={{ duration: p.dur, delay: p.delay, repeat: Infinity }} />)}
      </svg>
      <motion.svg width="220" height="270" viewBox="0 0 260 320" style={{ position: "relative", zIndex: 2 }} animate={{ y: [-5, 5, -5] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
        <defs>
          <linearGradient id="lockGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#a78bfa" /><stop offset="100%" stopColor="#4f46e5" /></linearGradient>
          <radialGradient id="lockGlow" cx="50%" cy="60%" r="50%"><stop offset="0%" stopColor="rgba(167,139,250,0.7)" /><stop offset="100%" stopColor="rgba(167,139,250,0)" /></radialGradient>
          <pattern id="dots" width="6" height="6" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="0.8" fill="rgba(167,139,250,0.55)" /></pattern>
        </defs>
        <motion.ellipse cx="130" cy="200" rx="120" ry="120" fill="url(#lockGlow)" animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }} />
        <path d="M70 140 V100 a60 60 0 0 1 120 0 V140" stroke="url(#lockGrad)" strokeWidth="10" fill="none" strokeLinecap="round" />
        <rect x="50" y="140" width="160" height="140" rx="18" fill="url(#dots)" stroke="url(#lockGrad)" strokeWidth="2.5" />
        <motion.g animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 2.4, repeat: Infinity }}><circle cx="130" cy="200" r="14" fill="#a78bfa" /><rect x="125" y="208" width="10" height="28" fill="#a78bfa" /></motion.g>
        {[0, 1, 2, 3].map((i) => <motion.line key={i} x1={50 + i * 40} y1={140} x2={50 + i * 40} y2={280} stroke="rgba(167,139,250,0.35)" strokeWidth="1" animate={{ opacity: [0, 1, 0] }} transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }} />)}
      </motion.svg>
      <div style={{ position: "absolute", bottom: 15, width: 280, height: 34, background: "radial-gradient(ellipse at center, rgba(139,92,246,0.5), rgba(139,92,246,0) 70%)", filter: "blur(4px)" }} />
    </div>
  );
};

const Modal = ({ open, onClose, title, children }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(2,3,15,0.7)", backdropFilter: "blur(8px)", zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <motion.div initial={{ scale: 0.94, y: 18, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }} transition={{ type: "spring", damping: 22, stiffness: 260 }} onClick={(e) => e.stopPropagation()} style={{ width: "min(480px, 100%)", background: "linear-gradient(180deg, #14163a, #0a0c24)", border: `1px solid ${colors.panelBorder}`, borderRadius: 18, padding: 24, color: colors.text, boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 60px rgba(139,92,246,0.15)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{title}</h3>
            <button onClick={onClose} style={{ background: "transparent", border: "none", color: colors.textMuted, cursor: "pointer", padding: 4 }} aria-label="Close"><Icon name="x" size={18} /></button>
          </div>
          {children}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const Drawer = ({ open, onClose, title, children }) => (
  <AnimatePresence>
    {open && (
      <>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(2,3,15,0.6)", backdropFilter: "blur(6px)", zIndex: 70 }} />
        <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 26, stiffness: 220 }} style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(380px, 100%)", background: "linear-gradient(180deg, #11132e, #07081a)", borderLeft: `1px solid ${colors.panelBorder}`, color: colors.text, zIndex: 71, padding: 24, overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{title}</h3>
            <button onClick={onClose} style={{ background: "transparent", border: "none", color: colors.textMuted, cursor: "pointer" }} aria-label="Close"><Icon name="x" size={18} /></button>
          </div>
          {children}
        </motion.aside>
      </>
    )}
  </AnimatePresence>
);

const ssoIcon = (name) => {
  if (name === "ms") return <svg width="18" height="18" viewBox="0 0 24 24"><rect x="2" y="2" width="9" height="9" fill="#f25022" /><rect x="13" y="2" width="9" height="9" fill="#7fba00" /><rect x="2" y="13" width="9" height="9" fill="#00a4ef" /><rect x="13" y="13" width="9" height="9" fill="#ffb900" /></svg>;
  if (name === "google") return <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M23 12.3c0-.8-.1-1.5-.2-2.3H12v4.4h6.2c-.3 1.4-1.1 2.6-2.4 3.4v2.8h3.9c2.3-2.1 3.6-5.2 3.6-8.3z" /><path fill="#34A853" d="M12 23c3.2 0 5.9-1 7.9-2.9l-3.9-2.8c-1 .7-2.4 1.1-4 1.1-3 0-5.6-2-6.5-4.7H1.5v3C3.5 20.8 7.4 23 12 23z" /><path fill="#FBBC05" d="M5.5 13.7c-.2-.7-.4-1.5-.4-2.2s.1-1.5.4-2.2V6.3H1.5C.5 8 0 9.9 0 12s.5 4 1.5 5.7l4-3z" /><path fill="#EA4335" d="M12 4.7c1.7 0 3.3.6 4.5 1.7l3.4-3.4C17.9 1 15.2 0 12 0 7.4 0 3.5 2.2 1.5 6.3l4 3C6.4 6.7 9 4.7 12 4.7z" /></svg>;
  if (name === "saml") return <Icon name="shield" size={18} color="#a78bfa" />;
  return <Icon name="spark" size={18} color="#a78bfa" />;
};

const inputWrap = (hasError) => ({
  position: "relative", display: "flex", alignItems: "center", background: colors.inputBg,
  border: `1px solid ${hasError ? "#ef4444" : colors.inputBorder}`, borderRadius: 10, padding: "0 12px", height: 44, transition: "border-color 180ms ease, box-shadow 180ms ease",
});

const inputStyle = { flex: 1, background: "transparent", border: "none", outline: "none", color: colors.text, fontSize: 14, fontFamily: FONT_STACK, padding: "0 8px" };

/* ---------- Static lookups (replace nested ternaries / && chains) ---------- */
const SSO_TITLES = {
  ms: "Microsoft Entra ID",
  google: "Google Workspace SSO",
  saml: "SAML 2.0 Configuration",
  ent: "Enterprise Directory Gateway",
};

const SSO_BODIES = {
  ms: "Securely routing your session through Microsoft Entra ID integration...",
  google: "Authenticating via Google Workspace Identity Protocol...",
  saml: "Please provide your organization's SAML 2.0 Metadata Endpoint to initiate secure handshake.",
  ent: "Provide your globally unique Enterprise ID to connect with your internal IAM.",
};

const SSO_PLACEHOLDERS = {
  saml: "https://idp.company.com/metadata.xml",
  ent: "CORP-ID-XXXXX",
};

const LEGAL_BODIES = {
  privacy: "We respect your privacy. This summary outlines how I-PQSign collects, processes, and protects your data in strict alignment with GDPR, DPDP, and ISO 27001 compliance architectures.",
  terms: "By authenticating into I-PQSign, you agree to our enterprise terms of use, including acceptable use policies, cryptographic licensing scope, and stringent SLAs.",
  support: "24/7 Priority Enterprise support is available through your dedicated Technical Account Manager. Use the internal SOC portal or escalate via admin@intrust.tech.",
};

const STRENGTH_LABELS = ["Too weak", "Weak", "Fair", "Strong", "Excellent"];
const STRENGTH_COLORS = ["#52557a", "#ef4444", "#f59e0b", "#10b981", "#22d3ee"];

/* ---------- Pure helpers (moved out of Login to cut its cognitive complexity) ---------- */
const getPasswordStrength = (pw) => {
  const rules = [pw.length >= 8, /[A-Z]/.test(pw), /\d/.test(pw), /[^A-Za-z0-9]/.test(pw)];
  return rules.filter(Boolean).length;
};

const validateLogin = (email, password) => {
  const errs = {};
  if (!email.trim()) errs.email = "Email is required";
  if (!password) errs.password = "Password is required";
  return errs;
};

const saveSession = (data, fallbackEmail) => {
  localStorage.setItem("token", data.token);
  localStorage.setItem("user", JSON.stringify(data.user));
  localStorage.setItem("userId", data.user.id);
  localStorage.setItem("email", data.user?.email || fallbackEmail);
  localStorage.setItem("identity_status", JSON.stringify(data.identity_status || {}));
};

const getPostLoginRoute = (data) => {
  const inviteToken = localStorage.getItem("inviteToken");
  if (inviteToken) return `/invite/${inviteToken}`;

  const s = data.identity_status;
  const verified = s?.email_verified || s?.mobile_verified || s?.aadhaar_verified;
  return verified ? "/dashboard" : "/select-identity";
};

/* ---------- Small sub-components extracted from Login ---------- */
const PasswordStrengthMeter = ({ password, capsOn }) => {
  if (!password) return null;
  const strength = getPasswordStrength(password);
  const color = STRENGTH_COLORS[strength];
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 3, marginBottom: 4 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 3, background: i < strength ? color : "rgba(255,255,255,0.08)", transition: "background 200ms ease" }} />
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: colors.textMuted }}>
        <span style={{ color }}>{STRENGTH_LABELS[strength]}</span>
        {capsOn && <span style={{ color: "#f59e0b" }}>⚠ Caps Lock is on</span>}
      </div>
    </div>
  );
};

const LanguageSelector = ({ lang, setLang }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button onClick={() => setOpen((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: "rgba(255,255,255,0.03)", border: `1px solid ${colors.panelBorder}`, borderRadius: 10, color: colors.text, cursor: "pointer", fontFamily: FONT_STACK, fontSize: 13, minWidth: 130, justifyContent: "space-between" }}>
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}><Icon name="globe" size={14} />{LANGS[lang].label}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }}><Icon name="chevron" size={12} /></motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.98 }} style={{ listStyle: "none", margin: 0, padding: 6, position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#0f1130", border: `1px solid ${colors.panelBorder}`, borderRadius: 10, zIndex: 50, boxShadow: "0 20px 40px rgba(0,0,0,0.5)", maxHeight: 280, overflowY: "auto" }}>
            {Object.keys(LANGS).map((k) => (
              <li key={k}>
                <button onClick={() => { setLang(k); setOpen(false); }} style={{ width: "100%", textAlign: "left", padding: "8px 10px", background: lang === k ? colors.accentSoft : "transparent", border: "none", color: colors.text, borderRadius: 7, cursor: "pointer", fontFamily: FONT_STACK, fontSize: 13 }}>
                  {LANGS[k].label}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

const ForgotPasswordModal = ({ open, onClose }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const handleClose = () => { onClose(); setState("idle"); setEmail(""); };
  const send = () => { setState("loading"); setTimeout(() => setState("success"), 1100); };

  return (
    <Modal open={open} onClose={handleClose} title="Reset Enterprise Password">
      {state === "success" ? (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(16,185,129,0.15)", margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center", color: "#10b981" }}>
            <Icon name="check" size={24} />
          </div>
          <p style={{ color: colors.textMuted, fontSize: 13 }}>If an account exists for <b style={{ color: colors.text }}>{email}</b>, a reset protocol has been initiated.</p>
        </div>
      ) : (
        <>
          <p style={{ color: colors.textMuted, fontSize: 13, marginTop: 0 }}>Enter your registered enterprise email to receive a secure recovery token.</p>
          <div style={inputWrap()}>
            <Icon name="mail" size={15} color={colors.textMuted} />
            <input type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
          </div>
          <button onClick={send} disabled={!email || state === "loading"} style={{ width: "100%", marginTop: 14, height: 44, border: "none", borderRadius: 10, color: "white", background: "linear-gradient(90deg,#4f46e5,#7c3aed)", cursor: "pointer", fontFamily: FONT_STACK, fontSize: 13, fontWeight: 600 }}>
            {state === "loading" ? "Initializing..." : "Send Recovery Token"}
          </button>
        </>
      )}
    </Modal>
  );
};

/* ============================================================================
 * MAIN COMPONENT
 * ========================================================================== */
export default function Login() {
  const navigate = useNavigate();

  const [lang, setLang] = useState("en");
  const t = LANGS[lang].dict;
  const legalTitles = { privacy: t.privacy, terms: t.terms, support: t.support };

  const [helpOpen, setHelpOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const [ssoModal, setSsoModal] = useState(null);
  const [legalModal, setLegalModal] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");

    const errs = validateLogin(email, password);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}/api/login`, { email, password });

      if (res.status !== 200 || !res.data.token) {
        setMessage("❌ Login failed. Please try again.");
        return;
      }

      saveSession(res.data, email);
      navigate(getPostLoginRoute(res.data));
    } catch (err) {
      console.error("Login error:", err);
      setMessage(err?.response?.data?.error || "❌ Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  /* ------------- HIGH-DENSITY 3-COLUMN ENTERPRISE STYLES ------------- */
  const page = {
    fontFamily: FONT_STACK,
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    color: colors.text,
    background: `radial-gradient(1000px 500px at 15% 0%, #1a1450 0%, transparent 60%),
                 radial-gradient(800px 400px at 90% 100%, #2a1066 0%, transparent 55%),
                 linear-gradient(180deg, ${colors.bgTop}, ${colors.bg})`,
    display: "flex",
    flexDirection: "column",
  };

  const topHeader = {
    height: "72px",
    flexShrink: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 56px",
    zIndex: 30,
  };

  const topControls = {
    display: "flex",
    gap: 16,
    alignItems: "center",
  };

  const mainGrid = {
    flex: 1,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr 410px",
    gap: "32px",
    padding: "0 56px",
    alignItems: "center",
    boxSizing: "border-box",
  };

  const textColumn = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };

  const graphicColumn = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  };

  const cardStyle = {
    background: colors.panel,
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: `1px solid ${colors.panelBorder}`,
    borderRadius: 20,
    padding: "26px 30px",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.06), 0 0 60px rgba(139,92,246,0.08)",
  };

  const bottomBar = {
    height: "76px",
    flexShrink: 0,
    padding: "0 56px",
    borderTop: `1px solid ${colors.panelBorder}`,
    background: "rgba(11, 12, 34, 0.65)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 30,
  };

  const badgeRow = {
    display: "flex",
    gap: "36px",
    alignItems: "center",
  };

  const badgeItem = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  };

  const iconContainer = {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: "rgba(139, 92, 246, 0.15)",
    border: `1px solid ${colors.panelBorder}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#c4b5fd",
  };

  const footerRight = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "6px",
  };

  const labelStyle = { fontSize: 12, fontWeight: 500, color: "#cfd2ee", marginBottom: 6, display: "block" };
  const ssoBtn = { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, height: 42, borderRadius: 10, background: "rgba(255,255,255,0.03)", border: `1px solid ${colors.panelBorder}`, color: colors.text, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: FONT_STACK, transition: "all 180ms ease" };

  const capability = (icon, title, desc, delay) => (
    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay, duration: 0.4 }} whileHover={{ x: 3 }} style={{ display: "flex", gap: 14, alignItems: "flex-start", cursor: "default" }}>
      <div style={{ width: 36, height: 36, minWidth: 36, borderRadius: 9, background: colors.accentSoft, border: `1px solid ${colors.panelBorder}`, display: "flex", alignItems: "center", justifyContent: "center", color: "#c4b5fd" }}>{icon}</div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.45 }}>{desc}</div>
      </div>
    </motion.div>
  );

  return (
    <div style={page}>
      <motion.div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 30% 40%, rgba(139,92,246,0.14), transparent 40%), radial-gradient(circle at 80% 70%, rgba(99,102,241,0.12), transparent 40%)", pointerEvents: "none" }} animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />

      {/* 1. TOP HEADER CONTROLS */}
      <div style={topHeader}>
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} whileHover={{ scale: 1.01 }} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
          <BrandMark />
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em" }}>
              I-PQSign <span style={{ background: "linear-gradient(90deg,#a78bfa,#6366f1)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}></span>
            </div>
            <div style={{ fontSize: 11, color: colors.textMuted }}>{t.tagline}</div>
          </div>
        </motion.div>

        <div style={topControls}>
          <LanguageSelector lang={lang} setLang={setLang} />

          <button onClick={() => setHelpOpen(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "transparent", border: "none", color: "#a78bfa", cursor: "pointer", fontFamily: FONT_STACK, fontSize: 13, fontWeight: 500 }}>
            <Icon name="help" size={15} />
            {t.needHelp}
          </button>
        </div>
      </div>

      {/* 2. MAIN 3-COLUMN CONTENT GRID */}
      <div style={mainGrid} className="pq-single-grid">
        {/* COLUMN 1: Hero Text and Features */}
        <div style={textColumn}>
          <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{ fontSize: "clamp(28px, 3.2vw, 44px)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.1, margin: "0 0 12px" }}>
            {t.heroA}<br />
            <span style={{ background: "linear-gradient(90deg,#a78bfa 0%, #7c3aed 60%, #6366f1 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t.heroB}</span>
            {t.heroC}
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.5 }} style={{ fontSize: 14, color: colors.textMuted, margin: "0 0 22px", lineHeight: 1.5, maxWidth: "440px" }}>
            {t.heroDesc}
          </motion.p>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {capability(<Icon name="shield-pulse" size={16} />, t.cap1Title, t.cap1Desc, 0.2)}
            {capability(<Icon name="atom" size={16} />, t.cap2Title, t.cap2Desc, 0.3)}
            {capability(<Icon name="compliance" size={16} />, t.cap3Title, t.cap3Desc, 0.4)}
            {capability(<Icon name="shield" size={16} />, t.cap4Title, t.cap4Desc, 0.5)}
          </div>
        </div>

        {/* COLUMN 2: Center Quantum Lock Graphic */}
        <div style={graphicColumn} className="pq-layered-visual">
          <QuantumLock />
        </div>

        {/* COLUMN 3: Right Login Card */}
        <div style={{ width: "100%", display: "flex", justifyContent: "center" }} className="pq-right">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} style={{ ...cardStyle, width: "100%" }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{t.welcome} <span>👋</span></h2>
            <p style={{ color: colors.textMuted, marginTop: 4, marginBottom: 20, fontSize: 13 }}>{t.signinSub}</p>

            <form onSubmit={handleLogin} noValidate>
              <label style={labelStyle} htmlFor="pq-email">{t.emailLabel}</label>
              <div style={inputWrap(!!errors.email)}>
                <Icon name="mail" size={15} color={colors.textMuted} />
                <input id="pq-email" type="email" placeholder={t.emailPh} value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} autoComplete="username" required />
              </div>
              {errors.email && <div style={errorTextStyle}>{errors.email}</div>}

              <div style={{ height: 14 }} />

              <label style={labelStyle} htmlFor="pq-pass">{t.passLabel}</label>
              <div style={inputWrap(!!errors.password)}>
                <Icon name="lock" size={15} color={colors.textMuted} />
                <input id="pq-pass" type={showPass ? "text" : "password"} placeholder={t.passPh} value={password} onChange={(e) => setPassword(e.target.value)} onKeyUp={(e) => setCapsOn(e.getModifierState && e.getModifierState("CapsLock"))} onKeyDown={(e) => setCapsOn(e.getModifierState && e.getModifierState("CapsLock"))} style={inputStyle} autoComplete="current-password" required />
                <button type="button" onClick={() => setShowPass((v) => !v)} style={{ background: "transparent", border: "none", color: colors.textMuted, cursor: "pointer", padding: 4 }} aria-label="Toggle password visibility">
                  <Icon name={showPass ? "eye-off" : "eye"} size={16} />
                </button>
              </div>
              {errors.password && <div style={errorTextStyle}>{errors.password}</div>}

              <PasswordStrengthMeter password={password} capsOn={capsOn} />

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: colors.textMuted }}>
                  <span onClick={() => setRemember((v) => !v)} style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${remember ? colors.accent : colors.inputBorder}`, background: remember ? colors.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 160ms ease" }}>
                    {remember && <Icon name="check" size={11} color="white" strokeWidth={3} />}
                  </span>
                  {t.remember}
                </label>
                <button type="button" onClick={() => setForgotOpen(true)} style={{ background: "transparent", border: "none", color: "#a78bfa", cursor: "pointer", fontSize: 12, fontWeight: 500, fontFamily: FONT_STACK }}>
                  {t.forgot}
                </button>
              </div>

              <motion.button type="submit" whileHover={{ scale: !loading ? 1.01 : 1 }} whileTap={{ scale: 0.99 }} disabled={loading} style={{ width: "100%", marginTop: 16, height: 46, borderRadius: 10, border: "none", cursor: !loading ? "pointer" : "default", fontFamily: FONT_STACK, fontSize: 14, fontWeight: 600, color: "white", background: "linear-gradient(90deg,#4f46e5 0%, #7c3aed 50%, #a855f7 100%)", boxShadow: "0 10px 25px -8px rgba(124,58,237,0.6), inset 0 1px 0 rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, position: "relative", overflow: "hidden" }}>
                {loading ? (
                  <motion.span style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", display: "inline-block" }} animate={{ rotate: 360 }} transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }} />
                ) : (
                  <>{t.signin} <Icon name="arrow" size={16} /></>
                )}
              </motion.button>

              {message && (
                <div style={{ ...errorTextStyle, textAlign: "center", marginTop: 12, fontSize: 12, padding: "6px 8px", background: "rgba(239, 68, 68, 0.1)", borderRadius: 6, border: "1px solid rgba(239, 68, 68, 0.2)" }}>
                  {message}
                </div>
              )}
            </form>

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, marginBottom: 14, color: colors.textMuted, fontSize: 12 }}>
              <div style={{ flex: 1, height: 1, background: colors.panelBorder }} />
              {t.continueWith}
              <div style={{ flex: 1, height: 1, background: colors.panelBorder }} />
            </div>

            {/* 🌟 2x2 SSO GRID */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <motion.button whileHover={{ y: -1, borderColor: colors.inputBorderFocus }} onClick={() => setSsoModal("ms")} style={ssoBtn}>
                {ssoIcon("ms")} {t.msSSO}
              </motion.button>
              <motion.button whileHover={{ y: -1, borderColor: colors.inputBorderFocus }} onClick={() => setSsoModal("google")} style={ssoBtn}>
                {ssoIcon("google")} {t.gSSO}
              </motion.button>
              <motion.button whileHover={{ y: -1, borderColor: colors.inputBorderFocus }} onClick={() => setSsoModal("saml")} style={ssoBtn}>
                {ssoIcon("saml")} {t.samlSSO}
              </motion.button>
              <motion.button whileHover={{ y: -1, borderColor: colors.inputBorderFocus }} onClick={() => setSsoModal("ent")} style={ssoBtn}>
                {ssoIcon("ent")} {t.enterprise}
              </motion.button>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: colors.textMuted, textAlign: "left" }}>
              {t.newTo}{" "}
              <button onClick={(e) => { e.preventDefault(); navigate("/register"); }} style={{ background: "transparent", border: "none", color: "#a78bfa", cursor: "pointer", fontFamily: FONT_STACK, fontSize: 12, fontWeight: 500, padding: 0 }}>
                {t.contactAdmin}
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* 3. SUBSTANTIAL ENTERPRISE FOOTER BAR */}
      <div style={bottomBar}>
        <div style={badgeRow}>
          <div style={badgeItem}>
            <div style={iconContainer}><Icon name="shield" size={18} /></div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{t.badge1Title}</div>
              <div style={{ fontSize: 11, color: colors.textMuted }}>{t.badge1Sub}</div>
            </div>
          </div>
          <div style={badgeItem}>
            <div style={iconContainer}><Icon name="iso" size={18} /></div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{t.badge2Title}</div>
              <div style={{ fontSize: 11, color: colors.textMuted }}>{t.badge2Sub}</div>
            </div>
          </div>
          <div style={badgeItem}>
            <div style={iconContainer}><Icon name="globe" size={18} /></div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{t.badge3Title}</div>
              <div style={{ fontSize: 11, color: colors.textMuted }}>{t.badge3Sub}</div>
            </div>
          </div>
        </div>

        <div style={footerRight}>
          <div style={{ display: "flex", gap: 20 }}>
            <button onClick={() => setLegalModal("privacy")} style={footerLinkStyle}>{t.privacy}</button>
            <button onClick={() => setLegalModal("terms")} style={footerLinkStyle}>{t.terms}</button>
            <button onClick={() => setLegalModal("support")} style={footerLinkStyle}>{t.support}</button>
          </div>
          <div style={{ fontSize: 12, color: colors.textMuted }}>{t.copyright}</div>
        </div>
      </div>

      {/* ============ MODALS / DRAWERS ============ */}
      <Drawer open={helpOpen} onClose={() => setHelpOpen(false)} title={t.needHelp}>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {["Platform Documentation", "API Reference Guide", "Contact Priority Support", "Open Security Ticket"].map((it) => (
            <li key={it}>
              <button onClick={() => setHelpOpen(false)} style={{ width: "100%", textAlign: "left", padding: "12px 14px", background: "rgba(255,255,255,0.03)", border: `1px solid ${colors.panelBorder}`, borderRadius: 10, color: colors.text, fontFamily: FONT_STACK, fontSize: 13, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {it} <Icon name="arrow" size={14} />
              </button>
            </li>
          ))}
        </ul>
      </Drawer>

      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />

      <Modal open={!!ssoModal} onClose={() => setSsoModal(null)} title={SSO_TITLES[ssoModal]}>
        <div style={{ color: colors.textMuted, fontSize: 13, lineHeight: 1.5 }}>{SSO_BODIES[ssoModal]}</div>
        {SSO_PLACEHOLDERS[ssoModal] && (
          <div style={{ marginTop: 14 }}>
            <div style={inputWrap()}>
              <Icon name="shield" size={15} color={colors.textMuted} />
              <input placeholder={SSO_PLACEHOLDERS[ssoModal]} style={inputStyle} />
            </div>
          </div>
        )}
        <button onClick={() => setSsoModal(null)} style={{ marginTop: 16, width: "100%", height: 44, border: "none", borderRadius: 10, cursor: "pointer", background: "linear-gradient(90deg,#4f46e5,#7c3aed)", color: "white", fontWeight: 600, fontFamily: FONT_STACK }}>
          Initiate Handshake
        </button>
      </Modal>

      <Modal open={!!legalModal} onClose={() => setLegalModal(null)} title={legalTitles[legalModal]}>
        <p style={{ color: colors.textMuted, fontSize: 13, lineHeight: 1.6 }}>{LEGAL_BODIES[legalModal]}</p>
      </Modal>

      <style>{`
        @media (max-width: 1100px) {
          .pq-single-grid { display: flex !important; flex-direction: column !important; overflow-y: auto !important; height: auto !important; padding: 20px !important; }
          .pq-right { order: -1; margin-bottom: 24px; }
          .pq-layered-visual { display: none !important; }
        }
      `}</style>
    </div>
  );
}

const footerLinkStyle = { background: "transparent", border: "none", color: colors.textMuted, cursor: "pointer", fontFamily: FONT_STACK, fontSize: 12, padding: 0 };
const errorTextStyle = { marginTop: 4, color: "#ef4444", fontSize: 11 };